// pages/MasterView.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, UserCog, Mail, Phone, Calendar, Clock,
    Loader2, CheckCircle, XCircle, Shield, Smartphone,
    CreditCard, Users, Award, Building, Wifi, WifiOff,
    Star, Video, PenTool, X, ImageOff, Download,
    MapPin
} from 'lucide-react';
import { getMasterById } from './AdminAPI';
import { timeAgo } from '../Masters/UiElements';

const InfoRow = ({ icon: Icon, label, value, valueClassName = '' }) => (
    <div className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-0">
        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
            <Icon size={16} className="text-orange-500" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 font-medium">{label}</p>
            <p className={`text-sm text-gray-800 break-words capitalize ${valueClassName}`}>
                {value === 0 ? 0 : (value || '—')}
            </p>
        </div>
    </div>
);

const Badge = ({ children, tone = 'gray' }) => {
    const tones = {
        gray: 'bg-gray-50 text-gray-600 border-gray-200',
        green: 'bg-green-50 text-green-700 border-green-200',
        red: 'bg-red-50 text-red-600 border-red-200',
        amber: 'bg-amber-50 text-amber-700 border-amber-200',
        blue: 'bg-blue-50 text-blue-700 border-blue-200',
    };
    return (
        <span className={`text-xs px-2.5 py-1 rounded-full font-medium border whitespace-nowrap ${tones[tone]}`}>
            {children}
        </span>
    );
};

// ---------- Media Modal ----------
const MediaModal = ({ media, onClose }) => {
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKeyDown);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = '';
        };
    }, [onClose]);

    if (!media) return null;
    const { type, url, title } = media;

    const handleDownload = async (url, fileName) => {
        if (!url) return;

        try {
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`Download failed: ${response.status}`);
            }

            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);

            // Clean up
            window.URL.revokeObjectURL(blobUrl);

        } catch (error) {
            console.error('Download error:', error);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-[fadeIn_0.15s_ease-out]"
            onClick={onClose}
        >
            <div
                className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                            {type === 'video' ? (
                                <Video size={15} className="text-orange-500" />
                            ) : (
                                <PenTool size={15} className="text-orange-500" />
                            )}
                        </div>
                        <p className="text-sm font-semibold text-gray-800 truncate">{title}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() =>
                                handleDownload(
                                    url,
                                    `${media.title || 'User'}.png`
                                )
                            }
                            className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                            title="Download"
                        >
                            <Download size={16} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
                            title="Close"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="bg-black flex items-center justify-center min-h-[240px] max-h-[75vh]">
                    {type === 'video' ? (
                        <video
                            src={url}
                            controls
                            autoPlay
                            className="w-full max-h-[75vh] bg-black"
                        >
                            Your browser does not support video playback.
                        </video>
                    ) : imgError ? (
                        <div className="text-center py-16 text-gray-400">
                            <ImageOff size={32} className="mx-auto mb-2" />
                            <p className="text-sm">Image could not be loaded</p>
                        </div>
                    ) : (
                        <img
                            src={url}
                            alt={title}
                            onError={() => setImgError(true)}
                            className="max-w-full max-h-[75vh] object-contain bg-white"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};



const DeviceCard = ({ device, index, onOpenMedia }) => {
    const enabledPerms = (device.permissions || []).filter((p) => p.enabled);

    return (
        <div className="bg-orange-50 rounded-xl border border-orange-100 p-4 shadow-md hover:shadow-md border-orange-300  hover:border-orange-300 transition-all">
            {/* Header */}
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center border border-orange-200 flex-shrink-0">
                        <Smartphone size={18} className="text-orange-600" />
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                            <p className="text-sm font-semibold text-gray-800 truncate">
                                {device.deviceName || `Device #${index + 1}`}
                            </p>
                            {device.isPrimary && (
                                <Star size={13} className="text-orange-500 fill-orange-500 flex-shrink-0" />
                            )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">{device.deviceType || '—'}</p>
                    </div>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <Badge tone={device.status === 'active' ? 'green' : 'red'}>
                        {device.status || 'unknown'}
                    </Badge>
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                        {device.isOnline ? (
                            <>
                                <Wifi size={12} className="text-green-500" /> Online
                            </>
                        ) : (
                            <>
                                <WifiOff size={12} className="text-gray-400" /> Offline
                            </>
                        )}
                    </span>
                </div>
            </div>

            {/* User info */}
            {device.userInfo && (
                <div className="bg-white rounded-lg border border-orange-100 p-3 mb-3">

                    {/* User Name */}
                    <div className="flex items-center gap-3 pb-2.5 mb-2.5 border-b border-orange-50">

                        <div className="w-8 h-8 rounded-lg bg-orange-50 border border-orange-100 flex items-center justify-center flex-shrink-0">
                            <Users size={14} className="text-orange-500" />
                        </div>

                        <div className="min-w-0">
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide">
                                User
                            </p>

                            <p className="text-sm font-semibold text-gray-800 truncate">
                                {device.userInfo.userName || '—'}
                            </p>
                        </div>

                    </div>


                    {/* Contact Information */}
                    <div className="grid grid-cols-12 gap-2">

                        {/* Mobile Number - 4 */}
                        <div className="col-span-4 flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-orange-50/50 border border-orange-50">

                            <div className="w-6 h-6 rounded-md bg-white border border-orange-100 flex items-center justify-center flex-shrink-0">
                                <Phone size={11} className="text-orange-500" />
                            </div>

                            <div className="min-w-0">
                                <p className="text-[9px] text-gray-400 uppercase tracking-wide">
                                    Mobile Number
                                </p>

                                <p className="text-xs font-medium text-gray-700 truncate">
                                    {device.userInfo.userPhone || '—'}
                                </p>
                            </div>

                        </div>

                        {/* Email - 8 */}
                        <div className="col-span-8 flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-orange-50/50 border border-orange-50">

                            <div className="w-6 h-6 rounded-md bg-white border border-orange-100 flex items-center justify-center flex-shrink-0">
                                <Mail size={11} className="text-orange-500" />
                            </div>

                            <div className="min-w-0 flex-1">
                                <p className="text-[9px] text-gray-400 uppercase tracking-wide">
                                    Email Address
                                </p>

                                <p
                                    className="text-xs font-medium text-gray-700 truncate"
                                    title={device.userInfo.userEmail || '—'}
                                >
                                    {device.userInfo.userEmail || '—'}
                                </p>
                            </div>

                        </div>

                    </div>

                </div>
            )}

            {/* Compact Meta Information */}
            <div className="grid grid-cols-4 gap-1.5 mb-2">

                {/* Device ID */}
                <div className="min-w-0 px-2 py-1.5 rounded-md bg-white border border-orange-500">
                    <div className="flex items-center gap-1 mb-0.5">
                        <Smartphone size={9} className="text-orange-500 flex-shrink-0" />
                        <span className="text-[8px] font-medium text-gray-400 uppercase tracking-wide truncate">
                            Device ID
                        </span>
                    </div>

                    <p
                        className="text-[10px] font-semibold text-orange-600 truncate"
                        title={device.deviceId || '—'}
                    >
                        {device.deviceId || '—'}
                    </p>
                </div>


                {/* Verified */}
                <div className="min-w-0 px-2 py-1.5 rounded-md bg-white border border-orange-500">
                    <div className="flex items-center gap-1 mb-0.5">
                        {device.isVerified ? (
                            <CheckCircle size={9} className="text-green-500 flex-shrink-0" />
                        ) : (
                            <XCircle size={9} className="text-red-400 flex-shrink-0" />
                        )}

                        <span className="text-[8px] font-medium text-gray-400 uppercase tracking-wide">
                            Verified
                        </span>
                    </div>

                    <p
                        className={`text-[10px] font-semibold truncate ${device.isVerified
                            ? 'text-green-600'
                            : 'text-red-500'
                            }`}
                    >
                        {device.isVerified ? 'Verified' : 'Not Verified'}
                    </p>
                </div>


                {/* Last Login */}
                <div className="min-w-0 px-2 py-1.5 rounded-md bg-white border border-orange-500">
                    <div className="flex items-center gap-1 mb-0.5">
                        <Clock size={9} className="text-orange-500 flex-shrink-0" />

                        <span className="text-[8px] font-medium text-gray-400 uppercase tracking-wide truncate">
                            Last Login
                        </span>
                    </div>

                    <p
                        className="text-[10px] font-semibold text-gray-700 truncate"
                        title={timeAgo(device.lastLogin)}
                    >
                        {timeAgo(device.lastLogin)}
                    </p>
                </div>


                {/* Last Connected */}
                <div className="min-w-0 px-2 py-1.5 rounded-md bg-white border border-orange-500">
                    <div className="flex items-center gap-1 mb-0.5">
                        {device.isOnline ? (
                            <Wifi size={9} className="text-green-500 flex-shrink-0" />
                        ) : (
                            <WifiOff size={9} className="text-gray-400 flex-shrink-0" />
                        )}

                        <span className="text-[8px] font-medium text-gray-400 uppercase tracking-wide truncate">
                            Connected
                        </span>
                    </div>

                    <p
                        className="text-[10px] font-semibold text-gray-700 truncate"
                        title={timeAgo(device.lastConnectedAt)}
                    >
                        {timeAgo(device.lastConnectedAt)}
                    </p>
                </div>


            </div>

            {/* Media buttons — open modal instead of new tab */}
            {(device.signatureImage || device.videoAuth) && (
                <div className="flex gap-2 mb-3">
                    {device.signatureImage && (
                        <button
                            type="button"
                            onClick={() =>
                                onOpenMedia({
                                    type: 'image',
                                    url: device.signatureImage,
                                    title: `Signature_${device.deviceName || `Device #${index + 1}`}`,
                                })
                            }
                            className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-white border border-orange-200 text-gray-600 hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                        >
                            <PenTool size={12} /> Signature
                        </button>
                    )}
                    {device.videoAuth && (
                        <button
                            type="button"
                            onClick={() =>
                                onOpenMedia({
                                    type: 'video',
                                    url: device.videoAuth,
                                    title: `Video_Authentication_${device.deviceName || `Device #${index + 1}`}`,
                                })
                            }
                            className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-white border border-orange-200 text-gray-600 hover:border-orange-400 hover:text-orange-600 hover:bg-orange-50 transition-colors"
                        >
                            <Video size={12} /> Video Auth
                        </button>
                    )}
                </div>
            )}

            {/* Permissions */}
            {enabledPerms.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {enabledPerms.map((perm) => (
                        <span
                            key={perm._id || perm.name}
                            className="text-[11px] px-2 py-0.5 rounded-md bg-white text-orange-600 border border-orange-100 font-medium"
                        >
                            {perm.name}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const SingleMaster = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [master, setMaster] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeMedia, setActiveMedia] = useState(null);

    useEffect(() => {
        const loadMaster = async () => {
            try {
                setLoading(true);
                setError('');
                const response = await getMasterById(id);
                if (response.success) {
                    setMaster(response.master);
                } else {
                    setError(response.message || 'Failed to load master details');
                }
            } catch (err) {
                setError(err?.response?.data?.message || err.message || 'Failed to load master');
            } finally {
                setLoading(false);
            }
        };
        if (id) loadMaster();
    }, [id]);

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <Loader2 size={40} className="animate-spin text-orange-500 mx-auto" />
                    <p className="mt-4 text-gray-600">Loading admin details...</p>
                </div>
            </div>
        );
    }

    if (error || !master) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
                <div className="text-center max-w-md">
                    <div className="text-red-500 text-5xl mb-4">⚠️</div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Master Not Found</h3>
                    <p className="text-gray-600 mb-6">{error || 'The requested admin could not be found'}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl hover:shadow-lg hover:shadow-orange-500/30 transition-all"
                    >
                        Back to Masters
                    </button>
                </div>
            </div>
        );
    }

    const devices = master.registeredDevices || [];
    const onlineCount = devices.filter((d) => d.isOnline).length;

    return (
        <div className="space-y-6 min-h-screen bg-gray-50 p-4 sm:p-6">
            <div className="">
                {/* Header */}
                <div className="flex items-center gap-3 sm:gap-4 mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-xl hover:bg-orange-100 transition-colors text-gray-600 hover:text-orange-600 flex-shrink-0"
                    >
                        <ArrowLeft size={22} />
                    </button>
                    <div className="min-w-0">
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-800 truncate">Master Admin Details</h1>
                        <p className="text-sm text-gray-500">View complete admin and plan information</p>
                    </div>
                </div>

                {/* Master Profile Card */}
                <div className="bg-white rounded-2xl shadow-xl border border-orange-200 overflow-hidden">
                    {/* Header with Avatar */}
                    <div className="px-4 sm:px-6 py-5 border-b border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-xl sm:text-2xl font-bold shadow-lg flex-shrink-0">
                                {master.name ? master.name.charAt(0).toUpperCase() : 'M'}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h2 className="text-lg sm:text-xl font-bold text-gray-800 truncate">{master.name}</h2>
                                    <Badge tone={master.planStatus === 'premium' ? 'amber' : 'gray'}>
                                        {master.planStatus || 'Free'} Plan
                                    </Badge>
                                    <Badge tone={master.masterStatus === 'active' ? 'green' : 'red'}>
                                        {master.masterStatus || 'unknown'}
                                    </Badge>
                                </div>
                                <p className="text-sm text-gray-500 mt-0.5 truncate">{master.email}</p>
                            </div>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="p-4 sm:p-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left Column - Personal Information */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                    <UserCog size={16} className="text-orange-500" />
                                    Admin Information
                                </h3>
                                <div className="bg-gray-50 rounded-xl border border-gray-200 divide-y divide-gray-100 px-4">
                                    <InfoRow icon={UserCog} label="Full Name" value={master.name} />
                                    <InfoRow icon={Mail} label="Email Address" value={master.email} valueClassName="normal-case" />
                                    <InfoRow icon={Phone} label="Phone Number" value={master.phoneNumber} />
                                    <InfoRow icon={Building} label="Role" value={master.role} />
                                    <InfoRow
                                        icon={Calendar}
                                        label="Joined On"
                                        value={master.createdAt ? new Date(master.createdAt).toLocaleDateString() : '—'}
                                    />
                                    <InfoRow icon={Clock} label="Last Login" value={timeAgo(master.lastLogin)} />
                                    <InfoRow
                                        icon={CheckCircle}
                                        label="Verified"
                                        value={master.isVerified ? 'Yes' : 'No'}
                                    />
                                    <InfoRow
                                        icon={CheckCircle}
                                        label="Terms Accepted"
                                        value={master.termsAccepted ? 'Yes' : 'No'}
                                    />
                                    <InfoRow
                                        icon={MapPin}
                                        label="Location"
                                        value={master.location || '—'}
                                    />
                                </div>
                            </div>

                            {/* Right Column - Plan & Device Information */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                                    <CreditCard size={16} className="text-orange-500" />
                                    Plan & Device Management
                                </h3>
                                <div className="bg-gray-50 rounded-xl border border-gray-200 divide-y divide-gray-100 px-4">
                                    <InfoRow icon={Award} label="Plan Status" value={master.planStatus || 'Free'} />
                                    <InfoRow icon={Smartphone} label="Max Devices Allowed" value={master.maxDevicesAllowed ?? 0} />
                                    <InfoRow icon={Users} label="Registered Devices" value={devices.length} />
                                    <InfoRow icon={Wifi} label="Currently Online" value={`${onlineCount} of ${devices.length}`} />
                                    <InfoRow
                                        icon={Shield}
                                        label="Device Limit Reached"
                                        value={master.deviceLimitReached ? 'Yes' : 'No'}
                                    />
                                    {master.planExpiresAt && (
                                        <InfoRow
                                            icon={Clock}
                                            label="Plan Expires"
                                            value={new Date(master.planExpiresAt).toLocaleDateString()}
                                        />
                                    )}
                                    <InfoRow
                                        icon={Clock}
                                        label="Last Updated"
                                        value={master.updatedAt ? new Date(master.updatedAt).toLocaleString() : '—'}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Devices Section */}
                        <div className="mt-6">
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <Smartphone size={16} className="text-orange-500" />
                                    Registered Devices ({devices.length})
                                </h3>
                                <span className="text-xs text-gray-500">
                                    Max: {master.maxDevicesAllowed ?? 0}
                                </span>
                            </div>

                            {devices.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {devices.map((device, index) => (
                                        <DeviceCard
                                            key={device._id || device.deviceId || index}
                                            device={device}
                                            index={index}
                                            onOpenMedia={setActiveMedia}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                                    <Smartphone size={32} className="text-gray-300 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">No devices registered yet</p>
                                    <p className="text-xs text-gray-400 mt-1">Devices will appear here once registered</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Media Modal (signature image / video auth) */}
            {activeMedia && <MediaModal media={activeMedia} onClose={() => setActiveMedia(null)} />}
        </div>
    );
};

export default SingleMaster;